import { InputBase, LabelProps } from '@/components/ReportForm/InputBase';
import { InputBaseType } from '@/components/ReportForm/InputBase/types';
import { useTranslation } from '@/i18n';
import {
  DatepickerProps,
  FormDatepicker,
  FormDatepickerProps,
} from '@moneyforward/ap-frontend-components';
import { DATE_FORMAT_WITH_TIME } from 'date-util';
import { memo, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { useGetPopupContainer } from '../Container/PopupContainer';

export type Props<TFieldValues extends FieldValues> =
  InputBaseType<TFieldValues> & FormDatepickerProps<TFieldValues>;

const InnerApReportFormInputTimeField = <TFieldValues extends FieldValues>({
  control,
  name,
  inputId,
  label,
  tooltip,
  hidden,
  caption,
  datepickerProps: _datePickerProps,
  required = false,
  ...rest
}: Props<TFieldValues>) => {
  const labelProps: LabelProps = useMemo(
    () => ({ required, children: label, tooltip }),
    [required, label, tooltip]
  );
  const { t } = useTranslation();
  const rules: { required: string | boolean } = useMemo(
    () => ({ required: required ? t('required') : false }),
    [required, t]
  );
  const getPopupContainer = useGetPopupContainer();
  const datePickerProps: DatepickerProps = useMemo(
    () => ({
      ..._datePickerProps,
      picker: 'date',
      showTime: true,
      single: true,
      format: DATE_FORMAT_WITH_TIME,
      getPopupContainer,
    }),
    [_datePickerProps, getPopupContainer]
  );

  return (
    <InputBase labelProps={labelProps} description={caption} hidden={hidden}>
      <FormDatepicker<TFieldValues>
        control={control}
        name={name}
        inputId={inputId}
        rules={rules}
        single
        format={DATE_FORMAT_WITH_TIME}
        {...rest}
        datepickerProps={datePickerProps}
      />
    </InputBase>
  );
};

InnerApReportFormInputTimeField.displayName = 'InnerApReportFormInputTimeField';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ApReportFormInputTimeField = memo((props) => (
  <InnerApReportFormInputTimeField {...props} />
)) as typeof InnerApReportFormInputTimeField;
ApReportFormInputTimeField.displayName = 'ApReportFormInputTimeField';
